import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { LandingComponent } from './components/landing/landing.component';
import { RunDetailComponent } from './components/run-detail/run-detail.component';
import { BlogDetailPanelComponent } from './components/blog-detail-panel/blog-detail-panel.component';
import { BrandConfigComponent } from './components/brand-config/brand-config.component';
import { StatusBadgeComponent } from './components/shared/status-badge.component';
import { ContentCardComponent } from './components/shared/content-card.component';
import { RunCardComponent } from './components/shared/run-card.component';
import { ConfirmDialogComponent } from './components/shared/confirm-dialog.component';
import { ReviewQueueComponent } from './components/review-queue/review-queue.component';
import { CMODashboardComponent } from './components/cmo-dashboard/cmo-dashboard.component';
import { CalendarGridComponent } from './components/calendar-grid/calendar-grid.component';
import { AgentSidebarComponent } from './components/agent-sidebar/agent-sidebar.component';
import { SettingsDrawerComponent } from './components/settings-drawer/settings-drawer.component';
import { NewsletterDetailPanelComponent } from './components/newsletter-detail-panel/newsletter-detail-panel.component';
import { SocialPostDetailPanelComponent } from './components/social-post-detail-panel/social-post-detail-panel.component';
import { PipelineComposerComponent } from './components/content-studio/pipeline-composer.component';
import { PipelineSourceCardComponent } from './components/content-studio/pipeline-source-card.component';
import { PipelineStepCardComponent } from './components/content-studio/pipeline-step-card.component';
import { ContentLibraryComponent } from './components/content-library/content-library.component';
import { AnalyticsSectionComponent } from './components/analytics/analytics-section.component';
import { MarketingDashboardV2Component } from './components/marketing-dashboard-v2/marketing-dashboard-v2.component';
import { SocialQueueSectionComponent } from './components/social-queue/social-queue-section.component';

// ─── Marketing Workspace (card #887 shell) ───
import { MarketingWorkspaceComponent } from './components/marketing-workspace/marketing-workspace.component';
import { CMOSupportChatComponent } from './components/marketing-workspace/cmo-support-chat/cmo-support-chat.component';
import { ComingSoonComponent } from './components/marketing-workspace/shared/coming-soon.component';
import { IconPairComponent } from './components/marketing-workspace/shared/icon-pair.component';
import { PageHeadComponent } from './components/marketing-workspace/shared/page-head.component';
import { PillComponent } from './components/marketing-workspace/shared/pill.component';
import { BrandStripeComponent } from './components/marketing-workspace/shared/brand-stripe.component';
import { HomeSection } from './components/marketing-workspace/sections/home.section';
import { StubSection } from './components/marketing-workspace/sections/stub.section';
import { StrategyDocSection } from './components/marketing-workspace/sections/strategy/strategy-doc.section';
import { StrategyPlanSection } from './components/marketing-workspace/sections/strategy/strategy-plan.section';
import { StrategyObjectivesSection } from './components/marketing-workspace/sections/strategy/strategy-objectives.section';
import { StrategyFoundationsSection } from './components/marketing-workspace/sections/strategy/strategy-foundations.section';
import { StrategyRecommendationsSection } from './components/marketing-workspace/sections/strategy/recommendations.section';
import { StrategySectionCardComponent } from './components/marketing-workspace/sections/strategy/strategy-section-card.component';
import { StrategySectionDiffModalComponent } from './components/marketing-workspace/sections/strategy/strategy-section-diff-modal.component';
import { ProgramsOverviewSection } from './components/marketing-workspace/sections/programs/programs-overview.section';
import { ProgramsBlogSection } from './components/marketing-workspace/sections/programs/programs-blog.section';
import { ProgramsSocialSection } from './components/marketing-workspace/sections/programs/programs-social.section';
import { ProgramsPodcastUploadSection } from './components/marketing-workspace/sections/programs/programs-podcast-upload.section';
import { FoundationApprovalsSection } from './components/marketing-workspace/sections/foundation/foundation-approvals.section';
import { FoundationActivityLogSection } from './components/marketing-workspace/sections/foundation/foundation-activity-log.section';
import { MentionDirectorySection } from './components/marketing-workspace/sections/foundation/mention-directory-section.component';
import { ExperimentsOverviewSection } from './components/marketing-workspace/sections/experiments/experiments-overview.section';
import { ExperimentsBacklogSection } from './components/marketing-workspace/sections/experiments/experiments-backlog.section';
import { ExperimentsProposeSection } from './components/marketing-workspace/sections/experiments/experiments-propose.section';
import { ExperimentsDetailSection } from './components/marketing-workspace/sections/experiments/experiments-detail.section';
import { ExperimentsSprintReviewSection } from './components/marketing-workspace/sections/experiments/experiments-sprint-review.section';
import { ExperimentsLearningsSection } from './components/marketing-workspace/sections/experiments/experiments-learnings.section';
import { ExperimentsLearningDetailSection } from './components/marketing-workspace/sections/experiments/experiments-learning-detail.section';
import { AnalyticsDataSourcesSection } from './components/marketing-workspace/sections/analytics/analytics-data-sources.section';
import { AdminUsersAndRolesSection } from './components/marketing-workspace/sections/admin/admin-users-and-roles.section';
import { StrategyOnboardingChatSection } from './components/marketing-workspace/sections/onboarding/strategy-onboarding-chat.section';
import { OnboardingPrerequisitesSection } from './components/marketing-workspace/sections/onboarding/prerequisites.section';
import { SkeletonComponent } from './components/marketing-workspace/shared/skeleton.component';
import { CardComponent } from './components/marketing-workspace/shared/card.component';
import { ensureWorkspaceRoutesRegistered } from './components/marketing-workspace/marketing-workspace.routes.config';

// Register routes once at module load.
ensureWorkspaceRoutesRegistered();

const WORKSPACE_DECLARATIONS = [
    MarketingWorkspaceComponent,
    CMOSupportChatComponent,
    ComingSoonComponent,
    IconPairComponent,
    PageHeadComponent,
    PillComponent,
    BrandStripeComponent,
    HomeSection,
    StubSection,
    StrategyDocSection,
    StrategyPlanSection,
    StrategyObjectivesSection,
    StrategyFoundationsSection,
    StrategyRecommendationsSection,
    StrategySectionCardComponent,
    StrategySectionDiffModalComponent,
    ProgramsOverviewSection,
    ProgramsBlogSection,
    ProgramsSocialSection,
    ProgramsPodcastUploadSection,
    FoundationApprovalsSection,
    FoundationActivityLogSection,
    MentionDirectorySection,
    ExperimentsOverviewSection,
    ExperimentsBacklogSection,
    ExperimentsProposeSection,
    ExperimentsDetailSection,
    ExperimentsSprintReviewSection,
    ExperimentsLearningsSection,
    ExperimentsLearningDetailSection,
    AnalyticsDataSourcesSection,
    AdminUsersAndRolesSection,
    StrategyOnboardingChatSection,
    OnboardingPrerequisitesSection,
    SkeletonComponent,
    CardComponent,
];

@NgModule({
    declarations: [
        CMODashboardComponent,
        MarketingDashboardV2Component,
        LandingComponent,
        RunDetailComponent,
        BlogDetailPanelComponent,
        BrandConfigComponent,
        StatusBadgeComponent,
        ContentCardComponent,
        RunCardComponent,
        ConfirmDialogComponent,
        ReviewQueueComponent,
        CalendarGridComponent,
        AgentSidebarComponent,
        SettingsDrawerComponent,
        NewsletterDetailPanelComponent,
        SocialPostDetailPanelComponent,
        PipelineComposerComponent,
        PipelineSourceCardComponent,
        PipelineStepCardComponent,
        ContentLibraryComponent,
        AnalyticsSectionComponent,
        SocialQueueSectionComponent,
        ...WORKSPACE_DECLARATIONS,
    ],
    imports: [
        CommonModule,
        FormsModule,
    ],
    exports: [
        LandingComponent,
        RunDetailComponent,
        BlogDetailPanelComponent,
        BrandConfigComponent,
        StatusBadgeComponent,
        ContentCardComponent,
        RunCardComponent,
        ConfirmDialogComponent,
        ReviewQueueComponent,
        CMODashboardComponent,
        CalendarGridComponent,
        AgentSidebarComponent,
        SettingsDrawerComponent,
        NewsletterDetailPanelComponent,
        SocialPostDetailPanelComponent,
        PipelineComposerComponent,
        PipelineSourceCardComponent,
        PipelineStepCardComponent,
        ContentLibraryComponent,
        AnalyticsSectionComponent,
        SocialQueueSectionComponent,
        MarketingDashboardV2Component,
        MarketingWorkspaceComponent,
    ]
})
export class ContentPipelineUIModule { }
